import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import {
  STATUS_LABELS,
  PRIORITAS_LABELS,
} from "../../data/programData";
import { ExternalLink, Lock, Info } from "lucide-react";
import PrioritasBadge from "./PrioritasBadge";
import StatusBadge from "./StatusBadge";

export default function EditSubKegiatanModal({
  open,
  onOpenChange,
  sub,
  canEdit,
  onSave,
}) {
  const [form, setForm] = useState(null);

  useEffect(() => {
    if (sub) setForm({ ...sub });
  }, [sub]);

  if (!sub || !form) return null;

  const isAudit = sub.specialType === "AUDIT_INVESTIGASI";
  const readOnly = !canEdit || isAudit;

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (readOnly) return;
    onSave(form);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="edit-sub-modal"
        className="max-w-xl max-h-[92vh] overflow-y-auto"
      >
        <DialogHeader>
          <div className="flex items-center gap-2">
            {sub.kategori ? (
              <span className="inline-flex h-5 items-center rounded bg-[#e8edf2] px-1.5 text-[10px] font-bold text-[#1e3a5f]">
                {sub.kategori}
              </span>
            ) : null}
            <span className="font-mono text-[11px] text-slate-500">{sub.id}</span>
          </div>
          <DialogTitle className="font-display text-lg leading-snug tracking-tight">
            {sub.nama}
          </DialogTitle>
          <DialogDescription className="text-[12px] text-slate-500">
            {canEdit ? "Ubah detail sub-kegiatan." : "Tampilan detail sub-kegiatan (hanya baca)."}
          </DialogDescription>
        </DialogHeader>

        {isAudit ? (
          <AuditInvestigasiPanel sub={sub} />
        ) : readOnly ? (
          <ReadOnlyPanel sub={sub} />
        ) : (
          <form
            onSubmit={handleSubmit}
            className="space-y-4"
            data-testid="edit-sub-form"
          >
            <div className="grid grid-cols-2 gap-3">
              <Field label="Target Mulai">
                <input
                  type="date"
                  value={form.targetMulai || ""}
                  onChange={(e) => setField("targetMulai", e.target.value)}
                  data-testid="edit-sub-target-mulai"
                  className="input-base"
                  required
                />
              </Field>
              <Field label="Target Selesai">
                <input
                  type="date"
                  value={form.targetSelesai || ""}
                  onChange={(e) => setField("targetSelesai", e.target.value)}
                  data-testid="edit-sub-target-selesai"
                  className="input-base"
                  required
                />
              </Field>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Prioritas">
                <select
                  value={form.prioritas}
                  onChange={(e) => setField("prioritas", e.target.value)}
                  data-testid="edit-sub-prioritas"
                  className="input-base"
                >
                  {Object.entries(PRIORITAS_LABELS).map(([k, v]) => (
                    <option key={k} value={k}>
                      {v}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Status">
                <select
                  value={form.status}
                  onChange={(e) => setField("status", e.target.value)}
                  data-testid="edit-sub-status"
                  className="input-base"
                >
                  {Object.entries(STATUS_LABELS).map(([k, v]) => (
                    <option key={k} value={k}>
                      {v}
                    </option>
                  ))}
                </select>
              </Field>
            </div>

            <Field label={`Progress (${form.progress || 0}%)`}>
              <input
                type="range"
                min="0"
                max="100"
                step="5"
                value={form.progress || 0}
                onChange={(e) => setField("progress", Number(e.target.value))}
                data-testid="edit-sub-progress"
                className="w-full accent-[#1e3a5f]"
              />
            </Field>

            <Field label="Catatan">
              <textarea
                value={form.catatan || ""}
                onChange={(e) => setField("catatan", e.target.value)}
                data-testid="edit-sub-catatan"
                rows={3}
                placeholder="Catatan tambahan (opsional)…"
                className="input-base resize-none"
              />
            </Field>

            <DialogFooter className="gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                data-testid="edit-sub-cancel"
              >
                Batal
              </Button>
              <Button
                type="submit"
                data-testid="edit-sub-save"
                className="bg-[#1e3a5f] text-white hover:bg-[#152a46]"
              >
                Simpan Perubahan
              </Button>
            </DialogFooter>
          </form>
        )}

        <style>{`
          .input-base {
            display: block;
            width: 100%;
            border-radius: 6px;
            border: 1px solid #d1d5db;
            background-color: #fff;
            padding: 8px 10px;
            font-size: 13px;
            color: #0f172a;
          }
          .input-base:focus {
            outline: none;
            border-color: #1e3a5f;
            box-shadow: 0 0 0 2px rgba(30, 58, 95, 0.2);
          }
        `}</style>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="mb-1 text-[11px] font-semibold uppercase tracking-[0.1em] text-slate-500">
        {label}
      </div>
      {children}
    </label>
  );
}

function DetailRow({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-3 border-b border-gray-100 py-2 last:border-b-0">
      <span className="text-[11px] font-semibold uppercase tracking-[0.1em] text-slate-500">
        {label}
      </span>
      <span className="text-right text-[12.5px] text-slate-800">{value}</span>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("id-ID", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

function ReadOnlyPanel({ sub }) {
  return (
    <div className="rounded-md border border-gray-200 bg-gray-50 p-4">
      <div className="mb-3 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
        <Lock className="h-3 w-3" />
        Akses hanya baca
      </div>
      <div className="rounded-md border border-gray-200 bg-white px-3 py-1">
        <DetailRow label="Target Mulai" value={formatDate(sub.targetMulai)} />
        <DetailRow label="Target Selesai" value={formatDate(sub.targetSelesai)} />
        <DetailRow
          label="Prioritas"
          value={<PrioritasBadge prioritas={sub.prioritas} />}
        />
        <DetailRow label="Status" value={<StatusBadge status={sub.status} />} />
        <DetailRow label="Progress" value={`${sub.progress || 0}%`} />
        <DetailRow
          label="Catatan"
          value={
            <span className="block max-w-[260px] whitespace-pre-wrap text-left text-[12px] text-slate-700">
              {sub.catatan?.trim() || "—"}
            </span>
          }
        />
      </div>
      <div className="mt-3 flex items-start gap-2 text-[11.5px] leading-relaxed text-slate-500">
        <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <span>
          Hanya peran <b>Irban</b> dan <b>Dalnis</b> yang dapat mengubah
          master data sub-kegiatan. Input progres kegiatan dilakukan di modul
          <i> Progress Kegiatan</i>.
        </span>
      </div>
    </div>
  );
}

function AuditInvestigasiPanel({ sub }) {
  return (
    <div className="rounded-md border border-amber-200 bg-amber-50 p-4">
      <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-amber-800">
        <Info className="h-3.5 w-3.5" />
        Kegiatan Khusus — Audit Investigasi
      </div>
      <p className="mt-2 text-[12.5px] leading-relaxed text-slate-700">
        Sub-kegiatan <b>{sub.kategori ? `${sub.kategori} — ` : ""}Tindak Lanjut
        Pemeriksaan Khusus / Audit Investigasi</b> dikelola melalui modul
        tersendiri yang akan dibangun di <b>Phase 6</b>. Input progres biasa
        tidak tersedia di sini.
      </p>
      <div className="mt-4 rounded-md border border-gray-200 bg-white px-3 py-1">
        <DetailRow label="Target Mulai" value={formatDate(sub.targetMulai)} />
        <DetailRow label="Target Selesai" value={formatDate(sub.targetSelesai)} />
        <DetailRow
          label="Prioritas"
          value={<PrioritasBadge prioritas={sub.prioritas} />}
        />
        <DetailRow label="Status" value={<StatusBadge status={sub.status} />} />
      </div>
      <div className="mt-4">
        <button
          type="button"
          data-testid="audit-investigasi-goto-btn"
          disabled
          title="Modul Audit Investigasi akan dibangun di Phase 6"
          className="inline-flex w-full items-center justify-center gap-2 rounded-md border border-amber-300 bg-white px-4 py-2.5 text-[13px] font-semibold text-amber-900 hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-80"
        >
          <ExternalLink className="h-4 w-4" />
          <span>→ Ke Modul Audit Investigasi</span>
          <span className="ml-2 rounded bg-amber-200 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-900">
            Phase 6
          </span>
        </button>
      </div>
    </div>
  );
}
